import Dialog from "components/ui/Dialog";
import Button from "components/ui/Button";
import { useTranslation } from "react-i18next";
import SuccessIcon from "../../../images/payment_check_circle.svg?react";
import FailedIcon from "../../../images/payment_failed.svg?react";
import { PricingModelItem } from "../../../services/pricingModel/model";
import { periodToString } from "services/pricingModel/utils";
import { formatMoney } from "utils/money";
import { useUserTimezone } from "utils/date";
import styles from "./PaymentResultModal.module.scss";

interface Props {
  variant: "success" | "failed";
  onClose: () => void;
  // The purchased plan — success only (drives the self-study receipt copy).
  plan?: PricingModelItem;
}

/** Payment outcome modal — the one component behind both the success and the
 *  failure result (they were a copy-pasted pair). */
export default function PaymentResultModal({ variant, onClose, plan }: Props) {
  const { t } = useTranslation();
  const withTimezone = useUserTimezone();
  const success = variant === "success";

  let text;
  if (!success) {
    text = <p>{t("Common.paymentResult.failedMessage")}</p>;
  } else if (plan?.purchase_student_type === "self-study") {
    const period = periodToString(t, plan.length);
    const nextPaymentDate = withTimezone.format(
      withTimezone.moment().add(plan.length, "days"),
      "fullDate"
    );
    text = (
      <p>
        {t("Common.paymentResult.successSelfStudy", {
          price: formatMoney(plan.price, plan.currency),
          period,
          date: nextPaymentDate,
        })}
      </p>
    );
  } else {
    text = <p>{t("Common.paymentResult.successOneOnOne")}</p>;
  }

  const title = success
    ? t("Common.paymentResult.successTitle")
    : t("Common.paymentResult.failedTitle");

  return (
    <Dialog
      open
      size="sm"
      header={null}
      title={title}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <div className={styles.container}>
        <div className={styles.actions}>
          {success ? <SuccessIcon /> : <FailedIcon />}
          <p className={styles.header}>{title}</p>
          {text}
          <Button variant={success ? "primary" : "danger"} size="large" onClick={onClose}>
            {success ? t("Common.actions.continue") : t("Common.actions.close")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
